"use client";

import { motion, useReducedMotion } from "framer-motion";

import { cn } from "@/lib/utils";
import { useLoopClock, useLoopVisibility } from "@/hooks/use-loop-clock";

/**
 * The practice loop, drawn as a loop.
 *
 * Four zig-zag screenshots used to explain this product, which is the
 * wrong shape: the whole claim is that the product *cycles*. So the
 * section shows a ring with a progress arc that fills one fifth per
 * stage, five nodes that light in turn, and the active stage written
 * out beside it. The ring is a sentence — you can see that step five
 * feeds back into step one.
 *
 * It runs on the same clock as the hero demo, so both are always
 * showing the same stage, and clicking a node or a row takes the loop
 * over: it jumps there and restarts that stage's dwell.
 */

const RADIUS = 118;
const CENTER = 150;

const nodePoint = (index: number, total: number) => {
  // Start at 12 o'clock, clockwise.
  const angle = (index / total) * Math.PI * 2 - Math.PI / 2;
  return {
    x: CENTER + Math.cos(angle) * RADIUS,
    y: CENTER + Math.sin(angle) * RADIUS,
  };
};

export function LoopDiagram() {
  const reduceMotion = useReducedMotion();
  const { stages, index, stage, seek } = useLoopClock();
  const visibilityRef = useLoopVisibility<HTMLDivElement>();

  const circumference = 2 * Math.PI * RADIUS;

  return (
    <div
      ref={visibilityRef}
      className="mt-14 grid items-center gap-12 md:mt-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-16"
    >
      {/* ── The ring ─────────────────────────────────────── */}
      <div className="relative mx-auto w-full max-w-[380px]">
        <div
          aria-hidden="true"
          className="loop-breathe absolute inset-6 rounded-full bg-primary/10 blur-3xl"
        />

        <svg
          viewBox="0 0 300 300"
          className="relative w-full"
          aria-hidden="true"
        >
          {/* Static track */}
          <circle
            cx={CENTER}
            cy={CENTER}
            r={RADIUS}
            fill="none"
            strokeWidth={1}
            className="stroke-border"
            strokeDasharray="3 7"
          />
          {/* Slow orbit, so the ring is never fully still */}
          <circle
            cx={CENTER}
            cy={CENTER}
            r={RADIUS - 14}
            fill="none"
            strokeWidth={1}
            strokeDasharray="2 12"
            className={cn("stroke-primary/25", !reduceMotion && "loop-spin")}
          />
          {/* Progress arc — one fifth more on every stage */}
          <circle
            cx={CENTER}
            cy={CENTER}
            r={RADIUS}
            fill="none"
            strokeWidth={3}
            strokeLinecap="round"
            className="stroke-primary"
            strokeDasharray={`${((index + 1) / stages.length) * circumference} ${circumference}`}
            transform={`rotate(-90 ${CENTER} ${CENTER})`}
            style={{
              transition: reduceMotion
                ? "none"
                : "stroke-dasharray 700ms cubic-bezier(0.16,1,0.3,1)",
            }}
          />

          {/* Centre: the loop's promise */}
          <text
            x={CENTER}
            y={CENTER - 6}
            textAnchor="middle"
            className="fill-foreground font-display text-[26px] font-semibold"
          >
            Daily
          </text>
          <text
            x={CENTER}
            y={CENTER + 16}
            textAnchor="middle"
            className="fill-muted-foreground text-[10px] uppercase tracking-[0.18em]"
            style={{ letterSpacing: "0.18em" }}
          >
            one loop
          </text>

          {/* Nodes. The ring is decoration — the list beside it carries the
              same five controls as real buttons, so nothing here needs to
              be focusable. */}
          {stages.map((s, i) => {
            const { x, y } = nodePoint(i, stages.length);
            const active = i === index;
            const Icon = s.icon;
            return (
              <g key={s.id}>
                <circle
                  cx={x}
                  cy={y}
                  r={30}
                  className={cn(
                    "transition-colors duration-500",
                    active
                      ? "fill-primary/10 stroke-primary/40"
                      : "fill-background stroke-border",
                  )}
                />
                {active && !reduceMotion && (
                  <circle
                    cx={x}
                    cy={y}
                    r={30}
                    fill="none"
                    strokeWidth={1.5}
                    className="loop-halo stroke-primary/50"
                  />
                )}
                <g transform={`translate(${x - 12} ${y - 12})`}>
                  <Icon
                    width={24}
                    height={24}
                    className={cn(
                      "transition-colors duration-500",
                      active ? "text-primary" : "text-muted-foreground",
                    )}
                  />
                </g>
              </g>
            );
          })}
        </svg>
      </div>

      {/* ── The stages, in words ─────────────────────────── */}
      <ol className="flex flex-col gap-1.5">
        {stages.map((s, i) => {
          const active = i === index;
          const Icon = s.icon;
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => seek(i)}
                aria-current={active ? "step" : undefined}
                className={cn(
                  "group flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-300 focus-ring",
                  active
                    ? "border-primary/30 bg-primary/[0.06]"
                    : "border-transparent hover:border-border hover:bg-muted/40",
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors duration-300",
                    active
                      ? "bg-primary/12 text-primary"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0">
                  <span className="flex items-baseline gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span
                      className={cn(
                        "text-[15px] font-semibold transition-colors duration-300",
                        active ? "text-foreground" : "text-foreground/75",
                      )}
                    >
                      {s.headline}
                    </span>
                  </span>
                  <motion.span
                    initial={false}
                    animate={{
                      height: active ? "auto" : 0,
                      opacity: active ? 1 : 0,
                      marginTop: active ? 6 : 0,
                    }}
                    transition={{
                      duration: reduceMotion ? 0 : 0.35,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="block overflow-hidden"
                  >
                    <span className="block max-w-md text-[13px] leading-relaxed text-muted-foreground">
                      {s.caption}
                    </span>
                  </motion.span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
